import { useState, useEffect, useRef } from 'react';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { TrendingUp, Users, Activity, DollarSign, AlertCircle } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const fmtDay = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const AXIS = { stroke: '#3A5673', fontSize: 11, fontFamily: 'IBM Plex Mono' };
const TIP = { backgroundColor: '#18324F', border: '1px solid #244463', borderRadius: 6, color: '#F2F6FB', fontFamily: 'IBM Plex Mono', fontSize: 12 };

// Measures its own width and passes explicit pixel dimensions to the chart,
// avoiding recharts' ResponsiveContainer width(-1)/height(-1) warnings entirely.
function ChartFrame({ height = 220, children }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} style={{ width: '100%', height }}>{w > 0 ? children(w, height) : null}</div>;
}

const Panel = ({ title, icon: Icon, children, right }) => (
  <div className="ops-card p-6 mb-5">
    <div className="flex items-center justify-between mb-5">
      <h2 className="ops-h2 flex items-center gap-2"><Icon size={15} style={{ color: 'var(--ops-text-2)' }} />{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

const Stat = ({ label, value }) => (
  <div className="ops-card p-3.5" style={{ background: 'var(--ops-input)' }}>
    <p className="ops-overline">{label}</p>
    <p className="ops-kpi-value mt-1.5" style={{ fontSize: '1.5rem' }}>{value}</p>
  </div>
);

export default function OpsAnalyticsPage() {
  const { opsAxios, OPS_API } = useOpsAuth();
  const [growth, setGrowth] = useState(null);
  const [eng, setEng] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [rev, setRev] = useState(null);

  useEffect(() => {
    const g = (p) => opsAxios({ method: 'GET', url: `${OPS_API}${p}` });
    g('/analytics/growth').then(r => setGrowth(r.data)).catch(console.error);
    g('/analytics/engagement').then(r => setEng(r.data)).catch(console.error);
    g('/analytics/funnel').then(r => setFunnel(r.data)).catch(console.error);
    g('/analytics/revenue').then(r => setRev(r.data)).catch(() => setRev({ connected: false }));
  }, [opsAxios, OPS_API]);

  return (
    <div data-testid="ops-analytics-page">
      <div className="mb-7">
        <h1 className="ops-h1">Analytics</h1>
        <p className="ops-sub mt-1">Growth, engagement, conversion &amp; revenue · last 30 days</p>
      </div>

      {/* GROWTH */}
      <Panel title="Growth" icon={TrendingUp}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Total users" value={growth?.total_users ?? '—'} />
          <Stat label="Onboarded" value={growth?.onboarded_users ?? '—'} />
          <Stat label="New · 30d" value={growth?.new_users_in_period ?? '—'} />
          <Stat label="Suspended" value={growth?.suspended_users ?? '—'} />
        </div>
        <ChartFrame height={220}>
          {(w, h) => (
            <AreaChart width={w} height={h} data={growth?.signups_series || []} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="gSign" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#244463" vertical={false} />
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={AXIS} tickLine={false} axisLine={{ stroke: '#244463' }} interval="preserveStartEnd" minTickGap={40} />
              <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TIP} labelFormatter={fmtDay} cursor={{ stroke: "#3A5673" }} />
              <Area type="monotone" dataKey="count" name="Signups" stroke="#60A5FA" strokeWidth={2} fill="url(#gSign)" />
            </AreaChart>
          )}
        </ChartFrame>
      </Panel>

      {/* ENGAGEMENT */}
      <Panel title="Engagement" icon={Activity}>
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3 mb-5">
          {[['DAU', eng?.dau], ['WAU', eng?.wau], ['MAU', eng?.mau],
            ['Cards', eng?.totals?.action_cards], ['Tasks', eng?.totals?.tasks],
            ['Meetings', eng?.totals?.meetings], ['Emails', eng?.totals?.emails_sent]
          ].map(([l, v]) => <Stat key={l} label={l} value={v ?? '—'} />)}
        </div>
        <ChartFrame height={220}>
          {(w, h) => (
            <LineChart width={w} height={h} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#244463" vertical={false} />
              <XAxis dataKey="date" type="category" allowDuplicatedCategory={false} data={eng?.cards_series || []}
                tickFormatter={fmtDay} tick={AXIS} tickLine={false} axisLine={{ stroke: '#244463' }} interval="preserveStartEnd" minTickGap={40} />
              <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TIP} labelFormatter={fmtDay} cursor={{ stroke: "#3A5673" }} />
              <Line data={eng?.cards_series || []} dataKey="count" name="Cards" stroke="#FAFAFA" strokeWidth={2} dot={false} />
              <Line data={eng?.tasks_series || []} dataKey="count" name="Tasks" stroke="#3B82F6" strokeWidth={2} dot={false} />
            </LineChart>
          )}
        </ChartFrame>
      </Panel>

      {/* FUNNEL */}
      <Panel title="Conversion funnel" icon={Users}>
        <div className="space-y-3.5">
          {(funnel?.steps || []).map((s, i) => (
            <div key={s.label} data-testid={`funnel-step-${i}`}>
              <div className="flex items-center justify-between mb-1.5 text-xs">
                <span style={{ color: 'var(--ops-text-2)' }}>{s.label}</span>
                <span className="ops-mono" style={{ color: 'var(--ops-muted)' }}>{s.count} · {s.pct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--ops-input)' }}>
                <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: '#FAFAFA', opacity: 1 - i * 0.18, transition: 'width .6s' }} />
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* REVENUE */}
      <Panel title="Revenue" icon={DollarSign}
        right={rev?.connected ? <span className="ops-badge ops-badge-success">Stripe · {rev.mode}</span> : <span className="ops-badge ops-badge-muted">Not connected</span>}>
        {rev?.connected ? (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
              <Stat label="MRR" value={`$${rev.mrr}`} />
              <Stat label="Subscribers" value={rev.active_subscribers} />
              <Stat label="Subscriptions" value={rev.active_subscriptions} />
            </div>
            {Object.keys(rev.plan_breakdown || {}).length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {Object.entries(rev.plan_breakdown).map(([k, v]) => (
                  <span key={k} className="ops-badge ops-badge-info">{k}: {v}</span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2.5 p-4 rounded-md" style={{ border: '1px dashed var(--ops-focus)' }}>
            <AlertCircle size={15} style={{ color: 'var(--ops-muted)' }} className="mt-0.5 shrink-0" />
            <div className="text-xs" style={{ color: 'var(--ops-text-2)' }}>
              Stripe is not connected. Set a real <span className="ops-mono">STRIPE_API_KEY</span> (sk_test_… / sk_live_…) to sync MRR, subscribers, and plan revenue.
              {rev?.error && <div className="mt-1" style={{ color: 'var(--ops-muted)' }}>Error: {rev.error}</div>}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
